import { inject, Injectable, NgZone } from '@angular/core';
import { Subject, Subscription, take } from 'rxjs';
import { AiApiService } from '../api/ai-api.service';
import { DiaApiService } from '../api/dia-api.service';
import { DIA_LIST_TITLE } from '../config/global';
import { MES_REQ_NUM } from '../constants/dialogue';
import { NO_LOGIN_KEY } from '../constants/storageKey';
import { IMesListGetBack, IMesRecordGroup } from '../types/mesRecords';
import { ICreateDiaReq, IUpdateDiaReq } from '../types/request/reqDia';
import { IAICommodityRes } from '../types/response/resAI';
import { IDiaListRes } from '../types/response/resDia';
import { useUUID } from '../utils/useUUID';
import { LoginStatusService } from './login-status.service';
import { UserCommonService } from './user-common.service';

@Injectable({
  providedIn: 'root'
})
export class MesRecordsService {

  private diaApi = inject(DiaApiService)
  private aiApi = inject(AiApiService)
  private userCSer = inject(UserCommonService)
  private loginStatusServe = inject(LoginStatusService)
  private nz = inject(NgZone)

  public initial$ = new Subject<number>()
  private updateDiaSub?: Subscription

  // 当前消息索引
  public gid: number = 0
  // 初始展示聊天记录的key
  public initialKey: string = ''
  // 聊天记录
  public mesRecords: IMesRecordGroup[] = []
  // 聊天记录加载状态
  private mesLoadingStatus: boolean = false

  // 新增对话列表
  public async createDiaList(loginStatus: boolean, id: number | string, key: string, type: 'current' | 'default' = 'current') {
    let params: ICreateDiaReq = {
      key: key,
      name: DIA_LIST_TITLE,
      header_img: 'default',
      session_id: ''
    }
    // 根据登录状态选择 未登录用nologinkey，登录使用wxid
    if (loginStatus) {
      params.wx_user_id = id as number
      params.user_id = type === 'current' ? this.userCSer.curContact?.id : this.userCSer.curDefContId()
    } else {
      params.no_login_key = id as string
    }

    let did = 0
    await this.diaApi.createDiaListAPI(params).then(res => { did = res.id })
    return did
  }

  // 修改对话内容
  public updateDia(params: IUpdateDiaReq) {
    this.updateDiaSub?.unsubscribe()
    this.updateDiaSub = this.diaApi.updateDiaAPI(params).pipe(take(1)).subscribe(res => res.session_id)
  }

  // 向本地MesServe添加一组消息数据
  public addOriginalMesList(
    { key = '', gid = 0, did = -1, title = DIA_LIST_TITLE,
      time = new Date(), create_time = new Date(), session_id = '', wxid = -1,
      user_id = -1, no_login_key = '', direc = 'end',
      first_reply = '', total = 0, residue = 0, loading_record_num = 0 } = {}
  ) {
    const params = {
      key, gid, did, title, time, create_time, session_id, wxid, user_id,
      no_login_key, records: [], first_reply, total, residue, loading_record_num
    }
    if (direc === 'end') {
      this.mesRecords.push(params)
    } else if (direc === 'front') {
      this.mesRecords.forEach(mes => { mes.gid++ })
      this.mesRecords.unshift(params)
    }

    // 返回当前存储消息mes的最后索引
    return this.mesRecords.length - 1
  }

  // 将数据处理至本地的mes，通过存储的MesKey向上下文中获取对应内容
  public async refreshLocalMesList(dia: IDiaListRes, dIndex: number) {
    // 新建临时对话内容
    const tempMes = {
      key: dia.key,
      gid: dIndex,
      did: dia.id,
      time: new Date(dia.updated_at),
      create_time: new Date(dia.session_create_time || dia.created_at),
      session_id: dia.session_id,
      wxid: dia.wx_user_id,
      user_id: dia.user_id,
      no_login_key: dia.no_login_key,
      title: DIA_LIST_TITLE,
      records: [],
      total: 0,
      first_reply: '',
      residue: 0,
      loading_record_num: 0
    }

    // 存储首条聊天记录key
    !dIndex && (this.initialKey = dia.key)
    // 获取对话最近20条数据
    const res = await this.aiApi.getMesRecordsByKeyPage(dia.key, 0, MES_REQ_NUM)
    tempMes.total = res.total
    tempMes.residue = res.total - res.data.length
    tempMes.loading_record_num = res.data.length
    // 获取对话首条回复内容
    await this.aiApi.getMesRecordsByKeyPage(dia.key, Math.floor(res.total / 2) - 1, 2)
      .then((firstReply) => {
        if (firstReply.data.length) {
          tempMes.first_reply = firstReply.data[res.total % 2]?.Content || ''
        }
      })
    this.mesRecords.push(tempMes)

    for (let i = 0; i < res.data.length; i++) {
      this._refreshLocalMesDetail(res.data[i])
    }
  }

  // 登录数据处理
  public getDiaListsByLogin(wxid: number) {
    this.diaApi.getLoginDiaListAPI(wxid).pipe(take(1)).subscribe(async res => {
      // 传递对话数量
      this.initial$.next(res.length ? res.length : 1)

      // res数组没长度说明当前是没有对话的状态，需要新增并存进去，有则把对应数据进行处理
      if (!res.length) {
        this.userCSer.switchCurContact()
        let key = useUUID(0, 16)
        // 创建消息列表
        let did = await this.createDiaList(true, wxid, key)
        // 创建本地mes
        this.addOriginalMesList({ key, gid: 0, did, wxid: this.userCSer.userInfo?.wx_user_id, user_id: this.userCSer.curContact?.id })
      } else {
        // 有相应的对话数据，处理数据至mes，先存第一个，再依序存后面的
        res.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        await this.refreshLocalMesList(res[0], 0)
        this._initalDiaPageScrollToBottom()
        // 切换当前联系人为首条消息绑定的联系人
        this.userCSer.switchCurContact(res[0].user_id)

        for (let i = 1; i < res.length; i++) {
          await this.refreshLocalMesList(res[i], i)
        }

        // 小程序登录判断对话列表是否有不存在的成员
        if (this.loginStatusServe.isMiniProgram) {
          const curAllContsIds = this.userCSer.contsInfo?.map(cont => cont.id)
          const newId = this.userCSer.curDefContId()
          this.mesRecords.forEach(mes => {
            if (!curAllContsIds?.some(uid => uid === mes.user_id)) {
              // 当前对话中对应的已删除的成员全部修改成默认人
              mes.user_id = newId
              this.updateDia({ user_id: newId, id: mes.did })
            }
          })
        }
      }
    })
  }

  // 未登录数据处理
  public async getDiaListsByNotLogin(noLoginKey: string | null) {
    if (!noLoginKey) {
      noLoginKey = useUUID(0, 32)
      localStorage.setItem(NO_LOGIN_KEY, noLoginKey)
      // 创建一个新的对话内容并存储（新的对话内容在发了消息之后会收到session_id，判断当前对话是否有，没有需要存进去）
      let key = useUUID(0, 16)
      let did = await this.createDiaList(false, noLoginKey, key)
      this.addOriginalMesList({ key, gid: 0, did, no_login_key: noLoginKey })
      this.initial$.next(1)
    } else {
      this.diaApi.getNotLoginDiaListAPI(noLoginKey).pipe(take(1)).subscribe(async res => {
        this.initial$.next(res.length ? res.length : 1)
        res.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        for (let i = 0; i < res.length; i++) {
          await this.refreshLocalMesList(res[i], i)
          !i && this._initalDiaPageScrollToBottom()
        }
      })
    }
  }

  // 置空当前聊天数据
  public resetMesRecords() {
    this.mesRecords = []
    this.gid = 0
  }

  // 聊天界面滚动至顶部时获取聊天记录
  public async getResidueChatMessage(container: HTMLDivElement) {
    if (this.mesLoadingStatus) { return }

    // 获取当前聊天对话内容
    const M = this.mesRecords[this.gid]
    if (!M.residue) { return }

    if (container.scrollTop === 0) {
      this.mesLoadingStatus = true

      const oldScrollHeight = container.scrollHeight
      const pageNum = Math.ceil((M.total - M.residue) / MES_REQ_NUM)
      await this.aiApi.getMesRecordsByKeyPage(M.key, pageNum, MES_REQ_NUM).then((res) => {
        M.residue -= res.data.length
        M.loading_record_num += res.data.length
        // 处理数据
        this.nz.run(() => {
          res.data.forEach((item: IMesListGetBack) => { this._refreshLocalMesDetail(item, this.gid) })
          setTimeout(() => { container.scrollTop = container.scrollHeight - oldScrollHeight }, 0)
          this.mesLoadingStatus = false
        })
      })
    }
  }

  // 删除会话
  public deleteDialogue(idx: number, success: Function, error: Function) {
    const did = this.mesRecords[idx].did
    this.diaApi.deleteDiaAPI(did).pipe(take(1)).subscribe(res => {
      if (res && res === 'success') {
        this.mesRecords.splice(idx, 1)
        this.mesRecords.forEach((item, index) => { item.gid = index })
        success()
      } else { error() }
    })
  }

  // 给定上下文的一条消息记录，存入本地mes中
  private _refreshLocalMesDetail(mes: IMesListGetBack, pos?: number) {
    let items: IAICommodityRes[] = []
    // 如果商品数据不为空处理商品数据
    if (mes.Data && mes.Data !== '') {
      items = JSON.parse(mes.Data) as IAICommodityRes[]
    }
    // 获取到的消息详情以倒序形式呈现，此处以倒序形式插入
    this.mesRecords[(pos || pos === 0) ? pos : this.mesRecords.length - 1].records.unshift({
      role: mes.Role,
      content: mes.Content,
      time: new Date(mes.updated_at),
      id: mes.id,
      imageUrl: mes.ImageUrl,
      items
    })
  }

  // 初始化滚动聊天界面至底部
  private _initalDiaPageScrollToBottom() {
    const diaCon = document.getElementById('chat-main') || document.getElementById('pc-mes-container')
    if (diaCon) {
      setTimeout(() => {
        diaCon.scrollTop = diaCon.scrollHeight
        setTimeout(() => { diaCon.scrollTop = diaCon.scrollHeight }, 500)
      })
    }
  }

}
